import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PlayCircle, Star, Trophy, Swap, TrendUp, TrendDown } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { Card, TopList } from "./adminUi";

const RANGES = [30, 90, 180, 365];

export default function AdminSimInsights() {
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async (d = days) => {
        setBusy(true);
        try {
            const r = await api.get("/admin/sim-insights", { params: { days: d } }).then((r) => r.data);
            setData(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Nepodařilo se načíst sim analytiku");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(days); }, [days]);

    const named = (rows) => (rows || []).map((r) => ({
        id: r.name,
        label: `${r.name}${r.team ? ` · ${r.team}` : ""}`,
        position: r.position,
        count: r.count,
        pct: r.pct,
    }));

    return (
        <div className="space-y-6" data-testid="admin-sim-insights">
            <div className="flex items-center gap-2 flex-wrap font-mono text-[11px]">
                <span className="text-white/50">Období:</span>
                {RANGES.map((r) => (
                    <button
                        key={r}
                        type="button"
                        onClick={() => setDays(r)}
                        disabled={busy}
                        data-testid={`sim-range-${r}`}
                        className={`px-2 py-1 border uppercase tracking-widest text-[10px] ${days === r ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                    >
                        {r} d
                    </button>
                ))}
            </div>

            {!data ? (
                <div className="font-mono text-[11px] text-white/50">{busy ? "Načítám…" : "Žádná data"}</div>
            ) : (
                <>
                    <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sim-kpis">
                        <Card label="Simulací celkem" value={data.total_runs} icon={PlayCircle} accent="text-fa-yellow" testid="sim-kpi-total" />
                        <Card label="AI výběr" value={data.ai_runs} sub={`${data.manual_runs} manuálních`} icon={Star} accent="text-fa-mint" testid="sim-kpi-ai" />
                        <Card label="Ø bodů" value={data.avg_total_points ?? "—"} icon={Trophy} accent="text-fa-sky" testid="sim-kpi-avg" />
                        <Card label="Max bodů" value={data.max_total_points ?? "—"} icon={TrendUp} accent="text-fa-mint" testid="sim-kpi-max" />
                        <Card label="Min bodů" value={data.min_total_points ?? "—"} icon={TrendDown} accent="text-red-300" testid="sim-kpi-min" />
                    </section>

                    <p className="font-mono text-[10px] text-white/45 leading-relaxed border border-white/10 bg-[#0d0d0d] p-3">
                        „Vybráno AI" = % AI simulací, kde hráč skončil v úvodní 15. Vysoké % + vysoké kapitánství
                        znamená, že engine hráče systematicky upřednostňuje — kandidát na srovnání predikčního modelu.
                    </p>

                    <section className="grid md:grid-cols-2 gap-3">
                        <TopList title="Nejčastěji vybraní AI (úvodní squad)" rows={named(data.top_picked)} icon={Star} accent="text-fa-yellow" testid="sim-top-picked" />
                        <TopList title="Nejčastější kapitáni (% všech GW)" rows={named(data.top_captains)} icon={Trophy} accent="text-fa-mint" testid="sim-top-captains" />
                    </section>
                    <section className="grid md:grid-cols-2 gap-3">
                        <TopList title="Nejčastější přestupy DOVNITŘ" rows={named(data.top_transfers_in)} icon={Swap} accent="text-fa-sky" testid="sim-top-in" />
                        <TopList title="Nejčastější přestupy VEN" rows={named(data.top_transfers_out)} icon={Swap} accent="text-red-300" testid="sim-top-out" />
                    </section>
                    <section>
                        <TopList title="Nejčastěji ve finálním squadu (po přestupech)" rows={named(data.top_final_squad)} icon={PlayCircle} accent="text-white" testid="sim-top-final" />
                    </section>
                </>
            )}
        </div>
    );
}
